// Library Methods
import { useTranslation } from "react-i18next";

// Components
import { useTheme } from "@mui/material/styles";
import { DownloadIcon, HeartbeatIcon } from "src/assets/icons";
import {
  Callout,
  Chapter,
  GuideTable,
  Paragraph,
  SubHeading,
  WhoRow,
} from "../GuideText/GuideText";
import { MockStatusChip } from "../GuideMocks/PortalMocks";
import {
  AddHubWalkthrough,
  HubCardWalkthrough,
  InstallWalkthrough,
} from "../Walkthroughs/SetupWalkthroughs";

// Styles
import { getRequiredErrorColor } from "src/styles/mui/muiStylesHelper";

export const AddHubChapter = () => (
  <Chapter
    id="add-hub"
    eyebrow="Set up the hub"
    icon={<HeartbeatIcon width="16px" height="16px" />}
    title="1. Add a hub"
  >
    <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
    <Paragraph text="First, tell the portal that a hub is coming. You only give it a name. Pick a name that says where the PC is, so everyone knows which computer it is." />
    <AddHubWalkthrough />
  </Chapter>
);

export const InstallChapter = () => (
  <Chapter
    id="install"
    eyebrow="Set up the hub"
    icon={<DownloadIcon width="16px" height="16px" />}
    title="2. Install it on the hub PC"
  >
    <WhoRow
      items={[
        { kind: "you", text: "You can do this" },
        { kind: "it", text: "needs someone who can approve installs" },
      ]}
    />
    <Paragraph text="Do these steps <b>on the hub PC itself</b>: sign in to the Clinic Portal there and download the installer. You never type a code. The download already knows which hub it belongs to." />
    <Callout
      tone="warning"
      text="Download only when you are ready to install. The downloaded file works <b>once</b> and only for <b>24 hours</b>. If you download it again, the older file stops working."
    />
    <InstallWalkthrough />
    <SubHeading text="If setup stops" />
    <Paragraph text="Setup checks everything before it installs. If something is wrong, it says so and installs nothing. Here is what each message means:" />
    <GuideTable
      columns={["Setup says", "What to do"]}
      rows={[
        [
          "<b>This token has expired. Issue a new one in the portal.</b>",
          "The file is older than 24 hours. Download the installer again from the hub card and run the new file.",
        ],
        [
          "<b>This token has already been used to enroll a PC.</b>",
          "The file was already used once. Download it again and run the new file.",
        ],
        [
          "<b>RetinaLogik does not recognise this token.</b>",
          "The file was replaced by a newer download, or it was damaged. Use the newest download.",
        ],
        [
          "<b>This hub has been deactivated in the portal.</b>",
          "Someone deleted this hub. Add a new hub and download its installer.",
        ],
        [
          "<b>This PC could not reach RetinaLogik at ...</b>",
          "Check the PC's internet connection, then click Back and try again. If it keeps happening, ask IT to check the firewall.",
        ],
        [
          '<b>Setup asks for an "Enrollment token"</b>',
          "EmrHub.msi was run on its own, without enrollment.json next to it. Extract the whole zip into one folder and run EmrHub.msi from there.",
        ],
        [
          "<b>RetinaLogik EMR Hub was not installed</b>",
          "Setup undid everything, so the PC is unchanged. The reason was shown just before. Fix it and run setup again.",
        ],
      ]}
    />
  </Chapter>
);

export const HubCardChapter = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Chapter
      id="hub-card"
      eyebrow="Set up the hub"
      icon={<HeartbeatIcon width="16px" height="16px" />}
      title="3. Read the hub card"
    >
      <WhoRow items={[{ kind: "you", text: "You can do this" }]} />
      <Paragraph text="Each hub has a card on the <b>Data Hub Config</b> tab. It is the hub's health at a glance. Step through it:" />
      <HubCardWalkthrough />
      <SubHeading text="The status box" />
      <GuideTable
        columns={["You see", "It means"]}
        rows={[
          [
            <MockStatusChip text={t("Pending")} color="#E2772E" />,
            "The hub was added, but it is not installed on a PC yet. Do <toinstall>step 2</toinstall>.",
          ],
          [
            <MockStatusChip text="Active" color="#22BE39" />,
            "All good. The hub is installed and talking to RetinaLogik.",
          ],
          [
            <MockStatusChip
              text={`Active · ${t("{{count}} problem", { count: 2 })}`}
              color="#E2772E"
            />,
            "The hub works, but something needs fixing. See <toproblems>Problems and fixes</toproblems>.",
          ],
          [
            <MockStatusChip
              text="Revoked"
              color={getRequiredErrorColor(theme)}
            />,
            "The hub was deleted. It can no longer send anything.",
          ],
        ]}
      />
    </Chapter>
  );
};
